import { Fragment } from "react";
import type { Dre } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";
import { categoriasDaLinha, linhasVisiveis, percentual, valorDaCategoria } from "./linhas-do-dre";
import type { ColunaDoDre } from "./TabelaDoDre";

// Na impressão só o DRE aparece; muitos meses (modo Ano) pedem a folha deitada.
const css = (deitada: boolean) => `
@media print {
  body * { visibility: hidden !important; }
  [data-dre-impresso], [data-dre-impresso] * { visibility: visible !important; }
  [data-dre-impresso] { position: absolute; inset: 0 auto auto 0; width: 100%; }
  @page { size: A4 ${deitada ? "landscape" : "portrait"}; margin: 12mm; }
}`;

/** O DRE como documento (vira PDF pelo "Baixar PDF"): cabeçalho do laboratório, tabela e notas. */
export function DreImpresso({ colunas, periodo, fora }: { colunas: ColunaDoDre[]; periodo: string; fora: Dre["foraDoResultado"] }) {
  const lab = trpc.financeiro.configExtrato.ler.useQuery().data?.laboratorio;
  const dres = colunas.map((c) => c.dre);
  const comPct = colunas[colunas.length - 1]!;
  const foraItens = [...fora.saidas.categorias.map((c) => ({ ...c, valorCents: -c.valorCents })), ...fora.entradas.categorias];
  return (
    <div data-dre-impresso className="hidden bg-white p-2 text-[11px] text-black print:block">
      <style>{css(colunas.length > 4)}</style>
      <div className="mb-4 flex items-start justify-between gap-4 border-b border-zinc-300 pb-3">
        <div className="flex items-center gap-3">
          {lab?.logoUrl && <img src={lab.logoUrl} alt="" className="h-10 w-10 object-contain" />}
          <div>
            <p className="text-sm font-bold">{lab?.nome ?? "Laboratório"}</p>
            {lab?.cnpj && <p>CNPJ {lab.cnpj}</p>}
            {lab?.endereco && <p>{lab.endereco}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold">Demonstração do resultado (DRE gerencial)</p>
          <p>{periodo}</p>
          <p>Emitido em {new Date().toLocaleDateString("pt-BR")}</p>
        </div>
      </div>
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-zinc-400 text-left">
            <th className="py-1 pr-2">Linha</th>
            {colunas.map((c) => (
              <th key={c.chave} className="px-1 py-1 text-right">
                {c.rotulo}
              </th>
            ))}
            <th className="py-1 pl-1 text-right">% receita</th>
          </tr>
        </thead>
        <tbody>
          {linhasVisiveis(dres).map((l) => (
            <Fragment key={l.chave}>
              <tr className={l.total ? "border-t border-zinc-300 font-bold" : ""}>
                <td className="py-0.5 pr-2">{l.rotulo}{l.estimado?.(comPct.dre) ? " (estimado)" : ""}</td>
                {colunas.map((c) => (
                  <td key={c.chave} className="px-1 py-0.5 text-right tabular-nums">
                    {reais(l.valor(c.dre))}
                  </td>
                ))}
                <td className="py-0.5 pl-1 text-right tabular-nums">{percentual(l.valor(comPct.dre), comPct.dre)}</td>
              </tr>
              {categoriasDaLinha(l, dres).map((cat) => (
                <tr key={cat.id} className="text-[10px] text-zinc-600">
                  <td className="py-0 pl-4 pr-2">{cat.nome}</td>
                  {colunas.map((c) => (
                    <td key={c.chave} className="px-1 py-0 text-right tabular-nums">
                      {reais(valorDaCategoria(l, c.dre, cat.id))}
                    </td>
                  ))}
                  <td />
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
      {foraItens.length > 0 && (
        <div className="mt-4">
          <p className="font-bold">Fora do resultado (mexeram no caixa, não no resultado)</p>
          {foraItens.map((c) => (
            <p key={c.id} className="flex justify-between">
              <span>{c.nome}</span>
              <span className="tabular-nums">{reais(c.valorCents)}</span>
            </p>
          ))}
        </div>
      )}
      <p className="mt-4 text-[10px] text-zinc-500">
        DRE gerencial, por competência, gerado pelo DentalBook a partir dos lançamentos e da produção do laboratório. Não substitui as demonstrações contábeis.
      </p>
    </div>
  );
}
